// sheetNaming — pure helper för POSITION-baserad sheet-numrering med
// circle-digits (①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳).
//
// Princip: sifferprefix lagras INTE i `sheet.name` — den deriveras från
// sheet:s INDEX vid render. När user tar bort sheet 2 får sheet 3
// automatiskt nummer 2 (index shift:as) utan att någon namn-state
// behöver uppdateras. User kan inte heller ändra siffran via rename.
//
// `sheet.name` lagrar BARA user-supplied text (t.ex. "Auth", "Database",
// eller tom sträng för onamngivna sheets). Display-namnet byggs av
// `getSheetDisplayName(name, index)` = `${circle} ${name}` (eller bara
// `${circle}` om namnet är tomt).
//
// Backward-compat: `stripCirclePrefix` används vid input-rensning så
// legacy-data med inbäddad prefix (`"① Auth"`) eller user som klistrar in
// `"⑤ Foo"` blir konsistent — bara user-text lagras.

const CIRCLE_DIGITS = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';

const NUMBER_TO_CIRCLE: Map<number, string> = new Map(
  Array.from(CIRCLE_DIGITS).map((ch, i) => [i + 1, ch]),
);

/** Returnerar circle-digit för n (1..20), eller `n.` som fallback för n>20. */
export function numberToCircle(n: number): string {
  if (n < 1) return '①';
  return NUMBER_TO_CIRCLE.get(n) ?? `${n}.`;
}

/**
 * Strippa leading circle-digit (och efterföljande whitespace) från ett
 * namn. Idempotent — namn utan prefix passerar oförändrade igenom.
 *
 * Användning:
 *  - Rensa user-input vid rename så prefix inte smyger in i state.
 *  - Migrera legacy-data där prefix var inbäddad (`"① Auth"` → `"Auth"`).
 *
 * Stödjer även numeriska prefixer (`"21. Foo"` → `"Foo"`) som
 * `numberToCircle` ger för n>20.
 */
export function stripCirclePrefix(name: string): string {
  // Circle-digit + valfri whitespace
  const circleStripped = name.replace(/^[①-⑳]\s*/, '');
  // Numerisk fallback (`"21. "` osv) för sheets bortom ⑳
  return circleStripped.replace(/^\d+\.\s*/, '');
}

/**
 * Beräkna display-namn för ett sheet vid given INDEX (0-baserad).
 * Format: `${circle(index+1)} ${userText}` — circle deriveras från
 * position, INTE lagrad. Tom user-text → bara circle.
 *
 * Idempotent: hanterar både ren user-text (`"Auth"`) och legacy-data
 * med inbäddad prefix (`"① Auth"`) — samma utfall i båda fallen.
 *
 * Exempel:
 *   getSheetDisplayName("Auth", 0)        → "① Auth"
 *   getSheetDisplayName("", 1)            → "②"
 *   getSheetDisplayName("① Auth", 2)      → "③ Auth"  (legacy-strippad)
 *   getSheetDisplayName("Auth", 20)       → "21. Auth" (efter ⑳)
 */
export function getSheetDisplayName(name: string, index: number): string {
  const userText = stripCirclePrefix(name).trim();
  const circle = numberToCircle(index + 1);
  return userText ? `${circle} ${userText}` : circle;
}
